import { Badge, Box, Card, Container, Fab, Typography } from "@mui/material";
import { ReactMediaRecorder } from "react-media-recorder";
import MicIcon from "@mui/icons-material/Mic";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

type Props = {
  label: string;
  required?: boolean;
  hasEvidence: () => boolean;
  getCachedRecording: () => string | null;
  onStop: (blobUrl: string, blob: Blob) => void | Promise<void>;
};

function getStatus(status: string) {
  return status !== "idle" ? status : "";
}

export default function VoiceRecorderCard({
  label,
  required,
  hasEvidence,
  getCachedRecording,
  onStop,
}: Props) {
  let cachedRecording: string | null = null;
  try {
    cachedRecording = getCachedRecording();
  } catch {
    cachedRecording = null;
  }

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Container>
        <Box display="flex" justifyContent="center">
          <Typography variant="body1">{label}</Typography>
        </Box>
      </Container>

      <ReactMediaRecorder
        audio
        onStop={(blobUrl: string, blob: Blob) => onStop(blobUrl, blob)}
        render={({
          status,
          startRecording,
          stopRecording,
          pauseRecording,
          resumeRecording,
          mediaBlobUrl,
        }) => {
          const restartRecording = async () => {
            await stopRecording();
            setTimeout(() => startRecording(), 300);
          };

          const audioSrc = mediaBlobUrl ?? cachedRecording ?? null;

          return (
            <>
              <Container sx={{ position: "relative" }}>
                {getStatus(status) === "recording" && (
                  <Box display="flex" justifyContent="right" px="2">
                    <Badge color="primary" variant="dot">
                      <MicIcon color="primary" />
                    </Badge>
                  </Box>
                )}

                {["", "stopped"].includes(getStatus(status)) && (
                  <Box display="flex" justifyContent="center" sx={{ py: 2 }}>
                    <Fab color="primary" size="large" onClick={startRecording}>
                      <MicIcon />
                    </Fab>
                  </Box>
                )}

                {["recording", "paused"].includes(getStatus(status)) && (
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    sx={{ py: 2 }}
                  >
                    <Fab
                      color="primary"
                      size="small"
                      onClick={restartRecording}
                    >
                      <AutorenewIcon />
                    </Fab>

                    {getStatus(status) === "recording" && (
                      <Fab
                        color="primary"
                        size="large"
                        onClick={pauseRecording}
                      >
                        <PauseIcon />
                      </Fab>
                    )}

                    {getStatus(status) === "paused" && (
                      <Fab
                        color="primary"
                        size="large"
                        onClick={resumeRecording}
                      >
                        <PlayArrowIcon />
                      </Fab>
                    )}

                    <Fab color="primary" size="small" onClick={stopRecording}>
                      <StopIcon />
                    </Fab>
                  </Box>
                )}
              </Container>

              {getStatus(status) === "stopped" && audioSrc && (
                <audio src={audioSrc} controls style={{ width: "100%" }} />
              )}
            </>
          );
        }}
      />

      {required && !hasEvidence() && (
        <Typography sx={{ mt: 1 }} color="error" variant="body2">
          This step is required. Please record audio to continue.
        </Typography>
      )}
    </Card>
  );
}
